import {
  Dimensions,
  PixelRatio,
  Platform,
  Text,
  TouchableOpacity,
} from 'react-native';
//import * as SecureStore from 'expo-secure-store';
import Storage from 'expo-native-storage';
import * as AppConstants from '@/utilities/AppConstants';
import 'react-native-get-random-values';
import { v4 as uuidv4 } from 'uuid';
import * as Linking from 'expo-linking';
import { UAParser } from 'ua-parser-js';
import DeviceInfo from 'react-native-device-info';
import * as Network from 'expo-network';

const MAX_DEBUG = 1000000;
const MAX_CONSOLE_LOG_SIZE = 9500;
const maxRemoteErrors = 5;
const maxLocalErrors = 10;
const storageKeyLocalData = "localData";
const FETCH_TIMEOUT_MILLISECONDS_MAIN = 60000;

let accumulatedDebug = "";
let urlMappings = {};
let errorsProcessed = 0;
let localData = null;
let debugLevelsRevert = null;

let DEBUGLEVEL = {
  network: AppConstants.DEFAULT_DEBUG_LEVEL,
  relativelyImportant: AppConstants.DEFAULT_DEBUG_LEVEL || AppConstants.IS_DEVELOPMENT,
  state: AppConstants.DEFAULT_DEBUG_LEVEL,
  uploads: AppConstants.DEFAULT_DEBUG_LEVEL,
  extra: AppConstants.DEFAULT_DEBUG_LEVEL,
  dev: AppConstants.IS_DEVELOPMENT,
  map: AppConstants.IS_DEVELOPMENT,
  layout: AppConstants.IS_DEVELOPMENT,
  beta: true,
  warnings: true, // Always keep warnings by default
};

const EXTRA_DEBUGLEVELS = {
  search: true,
};

function setExtraDebug(toggle) {
  if (toggle) {
    debugLevelsRevert = DEBUGLEVEL;
    for (const [k, v] of Object.entries(EXTRA_DEBUGLEVELS)) {
      DEBUGLEVEL[k] = v;
    }
  } else {
    DEBUGLEVEL = debugLevelsRevert;
  }
}

function appendToLog(str) {
  accumulatedDebug = accumulatedDebug + "\n" + str;

  // Limit how much memory debug is storing
  if (accumulatedDebug.length > MAX_DEBUG) {
    accumulatedDebug = "[... truncated ...] " + accumulatedDebug.substring(MAX_DEBUG / 2);
  }
}

function getLogPrefix() {
  return "[" + new Date().toISOString() + "] ";
}

function info(message, skipConsole) {
  const str = getLogPrefix() + message;
  if (!skipConsole) {
    console.log(str);
  }
  appendToLog(str);
}

function toJSONPretty(obj) {
  try {
    return JSON.stringify(obj, null, 2);
  } catch (e) {
    info("Error in toJSONPretty for " + (typeof obj) + " : " + e);
  }
}

function getMillisecondsAsSeconds(ms) {
  const millisecondsFloat = ms / 1000.0;
  let result = millisecondsFloat.toFixed(2);
  if (result.endsWith("00")) {
    result = result.substring(0, result.length - 3);
  }
  return result;
}

function getTruncatedPort(str) {
  const i = str.indexOf(':');
  if (i != -1) {
    str = str.substring(0, i);
  }
  return str;
}

function getNativeCurrentDomain() {
  const savedDomain = getLocalDataDomain();
  if (savedDomain && savedDomain.length > 0) {
    return savedDomain;
  } else {
    return null;
  }
}

/**
 * Gets the current host (e.g. rabbl.com,
 * somecommunity.rabbl.com, localhost:8081,
 * somecommunity.localhost:8081, localhost:3000, 
 * somecommunity.localhost:3000, etc.). There is no ending /.
 * 
 * @param truncatePort Whether or not to truncate any non-standard
 * port (e.g. on localhost:8081). A standard port (i.e. 80, 443)
 * will never be returned no matter this option.
 * 
 * @param frontend For localhost: if true, return port 8081;
 * otherwise, port 3000 (for backend).
 */
function getActualHost(truncatePort, frontend) {
  let result = AppConstants.IS_DEVELOPMENT ? "localhost:8081" : AppConstants.HOST;

  if (isWeb()) {
    if (global && global.window && global.window.location) {
      result = global.window.location.host;
      if (truncatePort) {
        result = getTruncatedPort(result);
      }
    }
  } else {
    let nativeCurrentDomain = getNativeCurrentDomain();
    if (nativeCurrentDomain && nativeCurrentDomain != AppConstants.HOST) {
      result = nativeCurrentDomain + "." + result;
    }
  }

  if (!frontend) {
    result = result.replace(':8081', ':3000');
  }

  if (!AppConstants.IS_DEVELOPMENT) {
    result = getTruncatedPort(result);
    result = result.replace("localhost", AppConstants.HOST)
    result = result.replace(/^192.*/, AppConstants.HOST)
  }

  //info("getActualHost: returning " + result);

  return result;
}

/**
 * Gets the base URL for the current host (e.g. https://rabbl.com,
 * https://somecommunity.rabbl.com, http://localhost:8081,
 * http://somecommunity.localhost:8081, http://localhost:3000
 * http://somecommunity.localhost:3000, etc.). There is no ending /.
 * 
 * @param onlyMainHost If true, exclude any subdomain (e.g. somecommunity.).
 * @param frontend For localhost: if true, return port 8081; otherwise, port 3000 (for backend).
 */
function getActualHostLink(onlyMainHost, frontend) {
  let result = getActualHost(false, frontend) ?? AppConstants.HOST;
  const isLocalhost = AppConstants.IS_DEVELOPMENT && result.indexOf('localhost') != -1;
  if (onlyMainHost) {
    if (!result.startsWith('localhost') && !result.startsWith(AppConstants.HOST)) {
      const i = result.indexOf('.');
      if (i != -1) {
        result = result.substring(i + 1);
      }
    }
  }
  if (!isLocalhost) {
    result = "https://" + result;
  } else {
    result = "http://" + result;
  }
  return result;
}

/**
 * Gets the logical host of the current context, i.e. either
 * the main host (i.e. rabbl.com) or a
 * community host without the rabbl.com suffix (e.g. somecommunity).
 * This will never have a port number. In other words, even
 * when developing locally on localhost, this will still
 * return rabbl.com|somecommunity. There is no ending /.
 * 
 * @param [keepFullHostForSubdomains=false] If true, keep the full host
 * for subdomains; for example, return somecommunity.rabbl.com
 * instead of somecommunity.
 */
function getLogicalHost(keepFullHostForSubdomains) {
  let result = getActualHost(true, false) ?? AppConstants.HOST;
  if (result.endsWith('localhost')) {
    result = result.substring(0, result.lastIndexOf('localhost')) + AppConstants.HOST;
  }

  if (result.endsWith(AppConstants.HOST) && result != AppConstants.HOST && !keepFullHostForSubdomains) {
    result = result.substring(0, result.lastIndexOf(AppConstants.HOST));
    if (result.endsWith('.')) {
      result = result.substring(0, result.lastIndexOf('.'));
    }
  }
  return result;
}

function debug(message) {
  const str = getLogPrefix() + message;
  if (str.length > MAX_CONSOLE_LOG_SIZE) {
    console.debug(str.substr(0, MAX_CONSOLE_LOG_SIZE) + " [...] TRUNCATED");
  } else {
    console.debug(str);
  }
  appendToLog(str);
}

function logException(e, context) {
  if (e) {
    if (context) {
      info("ERROR (" + context + "): " + getErrors(e));
    } else {
      info("ERROR: " + getErrors(e));
      dir(e);
    }
  }
}

let LOGICAL_USER_AGENT = null;
try {
  LOGICAL_USER_AGENT = UAParser();
} catch (e) {
  debug("Error parsing user agent: " + e);
}

function dir(obj) {
  try {
    if (isWeb()) {
      console.dir(obj);
    } else {
      console.debug(toJSONPretty(obj));
    }
    appendToLog(toJSONPretty(obj));
  } catch (e) {
    appendToLog("Error in dir: " + e);
  }
}

function isAndroid() {
  return Platform.OS === "android";
}

function isIOS() {
  return Platform.OS === "ios";
}

function isWeb() {
  return Platform.OS === "web";
}

function isWebAndroid() {
  if (isWeb()) {
    if (LOGICAL_USER_AGENT) {
      if (LOGICAL_USER_AGENT.os && LOGICAL_USER_AGENT.os.name && LOGICAL_USER_AGENT.os.name.startsWith("Android")) {
        return true;
      }
    }
  }
  return false;
}

function isWebiOS() {
  if (isWeb()) {
    if (LOGICAL_USER_AGENT) {
      if (LOGICAL_USER_AGENT.os && LOGICAL_USER_AGENT.os.name && LOGICAL_USER_AGENT.os.name.startsWith("iOS")) {
        return true;
      }
    }
  }
  return false;
}

function shouldAutoFocus() {
  return isWeb() && !isWebAndroid() && !isWebiOS();
}

function handleError(message, source) {
  try {
    throw new Error(message);
  } catch (e) {
    handleException(e, source);
  }
}

function handleException(
  e,
  source,
  snackContainer,
  additionalSetState = {
  },
) {
  handleErrors({
    explicitErrors: e,
    source: source,
    snackContainer: snackContainer,
    additionalSetState: additionalSetState,
  });
}

function reportIssue(message) {
  handleErrors({
    explicitErrors: message,
  });
}

function getClass(obj) {
  return obj && obj.constructor ? obj.constructor.name : "Unknown class";
}

// Either specify an explicit object or array in errors,
// or specify an errorsSource (such as a fetch response)
// that will be checked for various well-known error properties
// such as errorsSource.body.messages, errorsSource.errors
//
// If snackContainer is set, call setState({ snackVisible: true, snackText: errors, processing: false, ...additionalSetState });
// If errorsContainer is set, call setState({ errors: errors, processing: false, ...additionalSetState });
//
// If remoteLog is true, POST debug back to /api/debug
//
// We use console.log instead of console.error because Expo considers console.error as fatal
function handleErrors({
  explicitErrors = null,
  errorsSource = null,
  snackContainer = null,
  errorsContainer = null,
  additionalSetState = {
  },
  remoteLog = true,
  remoteData = null,
  source = "unknown",
} = {}) {

  errorsProcessed++;

  if (remoteLog && errorsProcessed > maxRemoteErrors) {
    // Don't want to overwhelm the network with error reports
    remoteLog = false;
  }

  if (errorsProcessed > maxLocalErrors) {
    //console.log(getLogPrefix() + "handleErrors: Reached maxLocalErrors");
    return;
  }

  try {
    // Before we do anything risky, log the errors to the console, if available
    let errors = "Unknown error";
    let stacks = "";
    if (explicitErrors) {
      errors = explicitErrors;
    } else if (errorsSource) {
      if (errorsSource.body && errorsSource.body.messages) {
        errors = errorsSource.body.messages;
      } else if (errorsSource.errors) {
        errors = errorsSource.errors;
      } else if (errorsSource.componentDidCatch) {
        errors = [errorsSource.componentDidCatch.error, errorsSource.componentDidCatch.errorInfo];
        if (errorsSource.componentDidCatch.errorInfo && typeof errorsSource.componentDidCatch.errorInfo === 'object') {
          for (let prop in errorsSource.componentDidCatch.errorInfo) {
            errors += prop + "=" + errorsSource.componentDidCatch.errorInfo[prop];
          }
        }
      }
    }

    let allErrors = getLogPrefix() + "handleErrors: Received errors (" + source + ")";

    if (errors) {
      allErrors += ": ";
      if (Array.isArray(errors)) {
        console.log("Error details (" + errors.length + ")");
        console.log(allErrors);
        console.log(errors);
        for (x of errors) {
          console.dir(x);
          const getErrorsResult = getErrors(x, true, true);
          allErrors += "\n\nDetails: " + getErrors(x, true, true);
          console.log(getErrorsResult);
        }
      } else {
        console.log("Error details:");
        console.dir(errors);
        const getErrorsResult = getErrors(errors, true, true);
        console.log(allErrors);
        console.log("getErrors result:");
        console.log(getErrorsResult);
        allErrors += "\n\nDetails: " + getErrorsResult;
      }
    }

    // Append the errors to our log
    appendToLog(allErrors);
    appendToLog(getLogPrefix() + "Finished handleErrors processing");

    if (remoteLog) {
      // Finally, we want to send this debug back to the server

      if (source) {
        allErrors += "\n\nSource: " + source;
      }

      // Add various context if we find it
      if (errorsSource) {
        if (errorsSource.status) {
          allErrors += "\n\nStatus: " + errorsSource.status;
        }
      }

      // Try to add in the state
      //try {
        // Causes issues serializing a lot of state
        //allErrors += "\n\nState: " + toJSONPretty(store.getState());
      //} catch (innerException) {
      //  allErrors += "\n\nState: Could not serialize: " + innerException;
      //}

      if (remoteData) {
        try {
          allErrors += "\n\nremoteData: " + toJSONPretty(remoteData);
        } catch (innerException) {
          allErrors += "\n\nremoteData: Could not serialize: " + innerException + " ; " + remoteData;
        }
      }

      allErrors += "\n\nLog:\n" + accumulatedDebug;

      // Skip reporting some errors. They'll still go to the console above.
      let shouldReportError = true;

      // Some checks are only done with a user agent on web
      if (isWeb() && window && window.navigator) {
        const ua = window.navigator.userAgent;
        if (ua) {
          if (ua.indexOf("bing.com/bingbot") != -1) {
            // These don't seem to make it to our backend, so this is
            // probably coming from CloudFlare. Reviewed and some bingbot
            // scrapes are going through, so not sure what to do with this.
            if (allErrors.indexOf("Error 403") != -1) {
              shouldReportError = false;
            }
          } else if (ua.indexOf("google.com/bot") != -1) {
            // Google Search console doesn't show any persistent errors,
            // so guessing this is either a forced error to see what happens
            // or some intermittent benign error.
            if (allErrors.indexOf("Failed to fetch") != -1) {
              shouldReportError = false;
            }
          } else if (ua.indexOf("baidu.com/search/spider") != -1) {
            if (allErrors.indexOf("Failed to fetch") != -1) {
              shouldReportError = false;
            }
          }
        }
      }

      if (shouldReportError) {
        sendDebug(allErrors);
      }
    }
  } catch (e) {
    console.log(getLogPrefix() + "handleErrors: Error processing", e);
  }
}

function getErrors(e, detailed, includeMaximumDetails) {
  let result = "";
  //info("getErrors:");
  //dir(e);
  if (e) {
    result = e.toString();
    try {
      if (e.message && !result.endsWith(e.message)) {
        result += " (message: " + e.message + ")";
      }
      if (detailed && e.status && !result.endsWith("Error " + e.status)) {
        result += " (status: " + e.status + ")";
      }
      if (e.code) {
        result += " (code: " + e.code + ")";
      }
      if (e.detailCode) {
        result += " (detailCode: " + e.detailCode + ")";
      }
      if (e.nativeEvent && e.nativeEvent.error) {
        result += " (nativeEvent.error: " + e.nativeEvent.error + ")";
      }
      if (detailed && e.messages_list && e.messages_list.length > 0) {
        result += " (messages list: " + e.messages_list.join("; ") + ")";
      }
      if (detailed && e.fileName) {
        result += " (file: " + e.fileName + ")";
      }
      if (detailed && e.lineNumber) {
        result += " (line: " + e.lineNumber + ")";
      }
      if (detailed && e.columnNumber) {
        result += " (column: " + e.columnNumber + ")";
      }
      if (detailed && e.name) {
        result += " (class: " + e.name + ")";
      } else if (detailed && e.constructor) {
        result += " (class: " + e.constructor.name + ")";
      }
      if (detailed && e.stack) {
        result += " (stack: " + e.stack + ")";
      }
      if (detailed && e.underlyingException) {
        result += " (underlying: " + getErrors(e.underlyingException, detailed) + ")";
      }

      if (includeMaximumDetails) {
        result += " (class: " + getClass(e) + ")";
      }

      // Replace some common text
      result = result.replaceAll("Error: Error", "Error");
      result = result.replaceAll("Network request failed", "Check your internet connection");
      result = result.replaceAll("The operation was aborted.", "Timed out after " + getMillisecondsAsSeconds(FETCH_TIMEOUT_MILLISECONDS_MAIN) + " seconds");
    } catch (innerException) {
      result += " (failed processing: " + innerException.toString() + ")";
    }
  } else {
    result = "Unexpected error";
  }
  return result;
}

async function sendDebugDirect(msg) {
  try {

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000 /* ms */);

    const result = await fetch(
      addCommonQueryParameters(getActualHostLink(false, false) + "/api/debug"),
      {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          //"Host": AppConstants.HOST,
        },
        body: JSON.stringify({
          message: msg,
        }),
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    return result;

  } catch (e) {
    info("sendDebugDirect failed with: " + getErrors(e));
    return null;
  }
}

// We use console.log instead of console.error because Expo considers console.error as fatal
async function sendDebug(msg) {
  try {
    console.log(getLogPrefix() + "sendDebug: Started");
    await sendDebugDirect(msg);
    console.log(getLogPrefix() + "sendDebug: Succeeded calling api/debug");
  } catch (e) {
    console.log(getLogPrefix() + "sendDebug: Failed call to api/debug", e);
  }
}

function getStoredValue(key) {
  return getStoredValueInsecure(key);
}

function getStoredValueSecure(key) {
  if (Platform.OS === "web") {
    //return await AsyncStorage.getItem(key);
    return Storage.getObjectSync(key);
  } else {
    //return await SecureStore.getItemAsync(key);
    return Storage.getObjectSync(key);
  }
}

function getStoredValueInsecure(key) {
  if (isWeb()) {
    //return await AsyncStorage.getItem(key);
    return Storage.getObjectSync(key);
  } else {
    //return await AsyncStorage.getItem(key);
    return Storage.getObjectSync(key);
  }
}

function setStoredValue(key, val) {
  setStoredValueInsecure(key, val);
}

function setStoredValueSecure(key, val) {
  if (Platform.OS === "web") {
    //return await setStoredValueWeb(key, val);
    Storage.setObjectSync(key, val);
  } else {
    // https://docs.expo.dev/versions/latest/sdk/securestore/
    // Size limit for a value is 2048 bytes
    //return await SecureStore.setItemAsync(key, val);
    Storage.setObjectSync(key, val);
  }
}

function setStoredValueInsecure(key, val) {
  if (isWeb()) {
    //return await setStoredValueWeb(key, val);
    Storage.setObjectSync(key, val);
  } else {
    // https://react-native-async-storage.github.io/async-storage/docs/limits/
    // 
    // Total storage size is capped at 6 MB by default. You can increase this size
    // by specifying a new size using feature flag.
    // 
    // Per-entry is limited by a size of a WindowCursor, a buffer used to read data
    // from SQLite. Currently it's size is around 2 MB. This means that the single
    // item read at one time cannot be larger than 2 MB. There's no supported
    // workaround from AsyncStorage. We suggest keeping your data lower than that,
    // by chopping it down into many entries, instead of one massive entry. This
    // is where multiGet and multiSet APIs can shine.
    //return await AsyncStorage.setItem(key, val);
    Storage.setObjectSync(key, val);
  }
}

function setStoredValueWeb(key, val) {
  // Limits:
  //
  // Android: https://react-native-async-storage.github.io/async-storage/docs/limits
  // "This means that the single item read at one time cannot be larger than 2 MB"
  // "Total storage size is capped at 6 MB by default"
  //
  // Web: https://en.wikipedia.org/wiki/Web_storage#Storage_size
  // "Safari 8 allows 5 MB'"
  //
  //return await AsyncStorage.setItem(key, val);
  Storage.setObjectSync(key, val);
}

function removeStoredValue(key) {
  if (isWeb()) {
    //return await AsyncStorage.removeItem(key);
    Storage.removeItemSync(key);
  } else {
    //return await SecureStore.deleteItemAsync(key);
    Storage.removeItemSync(key);
  }
}

function loadLocalData() {
  if (!localData) {
    const rawLocalData = getStoredValue(storageKeyLocalData);
    if (rawLocalData) {
      localData = JSON.parse(rawLocalData);
      if (DEBUGLEVEL.local) debug("localData: " + toJSONPretty(localData));
    } else {
      localData = {};
    }
  }
  return localData;
}

function updateLocalData(key, val) {
  ensureLocalData();
  localData[key] = val;
  setStoredValue(storageKeyLocalData, toJSONPretty(localData));
}

function removeLocalData() {
  localData = {};
  removeStoredValue(storageKeyLocalData);
}

function updateSavedAppState(appStateKey, appStateValue) {
  info("AppUtilities.updateSavedAppState updating " + appStateKey);

  ensureLocalData();
  let savedAppState = localData[AppConstants.LOCAL_DATA_KEYS.SAVED_APP_STATE];
  if (!savedAppState) {
    savedAppState = {};
  }
  savedAppState[appStateKey] = appStateValue;
  updateLocalData(AppConstants.LOCAL_DATA_KEYS.SAVED_APP_STATE, savedAppState);
}

function getSavedAppState(appStateKey, defaultValue) {
  ensureLocalData();
  let savedAppState = localData[AppConstants.LOCAL_DATA_KEYS.SAVED_APP_STATE];
  if (!savedAppState) {
    savedAppState = {};
  }

  let result = savedAppState[appStateKey];
  if (!result) {
    result = defaultValue;
  }
  return result;
}

function isUndefined(o) {
  return typeof o === 'undefined';
}

function isDefined(o) {
  return !isUndefined(o);
}

function isString(o) {
  return typeof o === 'string';
}

function isNull(obj) {
  return obj === null;
}

function isBlank(str) {
  return isUndefined(str) || isNull(str) || str.length == 0;
}

function isNullOrUndefined(x) {
  return isNull(x) || isUndefined(x);
}

function getLocalData(key, defaultValue) {
  ensureLocalData();
  let result = localData[key];
  if (isNullOrUndefined(result)) {
    result = defaultValue;
  }
  return result;
}

function getAllLocalData() {
  return localData;
}

function getLocalDataDomain() {
  ensureLocalData();
  return localData[AppConstants.LOCAL_DATA_KEYS.DOMAIN];
}

function setLocalDataDomain(domain) {
  info("AppUtilities.setLocalDataDomain: " + domain);
  updateLocalData(AppConstants.LOCAL_DATA_KEYS.DOMAIN, domain);
}

function login(responseJson) {
  info("AppUtilities.login: User logged in");
  responseJson.expires = new Date().getTime() + (responseJson.expires_in * 1000);
  updateLocalData(AppConstants.LOCAL_DATA_KEYS.REMOTE_SESSION, responseJson);
}

function ensureLocalData() {
  if (!localData) {
    loadLocalData();
  }
}

function getLocalDataRemoteSession() {
  ensureLocalData();
  const result = localData[AppConstants.LOCAL_DATA_KEYS.REMOTE_SESSION];
  if (DEBUGLEVEL.extra) {
    debug("getLocalDataRemoteSession = " + result);
  }
  return result;
}

function logout() {
  if (DEBUGLEVEL.extra) {
    debug("AppUtilities.logout called")
  }
  if (getLocalData(AppConstants.LOCAL_DATA_KEYS.REMOTE_SESSION)) {
    if (DEBUGLEVEL.extra) {
      debug("AppUtilities.logout clearing login data")
    }
    clearLogInData();
  }
}

function clearLogInData() {
  debug("clearLogInData entry");
  updateLocalData(AppConstants.LOCAL_DATA_KEYS.REMOTE_SESSION, null);

  // If they log out and then restart the app, it makes sense to start them in the same domain
  //updateLocalData(AppConstants.LOCAL_DATA_KEYS.DOMAIN, null);
  
  debug("clearLogInData exit");
}

function isAccessTokenExpired() {
  if (getLocalDataRemoteSession()) {
    const x = new Date(getLocalDataRemoteSession().expires);
    const y = new Date();
    if (DEBUGLEVEL.network) {
      info("isAccessTokenExpired expires: " + x + ", now: " + y);
    }
    return x < y;
  } else {
    return true;
  }
}

function hasAccessToken() {
  if (getLocalDataRemoteSession()) {
    return true;
  } else {
    return false;
  }
}

async function ensureValidAccessToken() {
  if (DEBUGLEVEL.network) debug("> ensureValidAccessToken " + toJSONPretty(getLocalDataRemoteSession()));

  if (isAccessTokenExpired()) {

    const url = getActualHostLink(false, false) + "/api/refresh_token";

    debug("ensureValidAccessToken: Requesting token refresh from " + url);

    let result = null;

    try {
      const response = await fetch(
        addCommonQueryParameters(url),
        {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            //"Host": HOST,
          },
          body: toJSONPretty({
            "refresh_token": getLocalDataRemoteSession().refresh_token,
          }),
        }
      );
      
      if (DEBUGLEVEL.network) debug("refresh_token raw response: " + toJSONPretty(response));
  
      const responseJson = await response.json();
  
      if (DEBUGLEVEL.network) debug("refresh_token JSON response: " + toJSONPretty(responseJson));
  
      if (responseJson.result) {
        login(responseJson);
        result = { successful: true, token: getLocalDataRemoteSession().token };
      } else {

        debug("ensureValidAccessToken: unsuccessful status = " + responseJson.status);

        // If there's some problem with the refresh token, require a relogin
        if (responseJson.status == 403) {
          // This can happen, for example, if the app logs into development
          // and then tries to log into production. In that case, we think
          // the token is still not expired, but when we try to use it,
          // it fails. It could also happen if the token database is cleared
          // for some reason. So in this case, just log the user out and
          // have them log back in.
          if (DEBUGLEVEL.network) debug("ensureValidAccessToken: 403; logging out");
  
          logout();
  
          result = { successful: false, errors: responseJson.messages, status: responseJson.status, failedRefresh: true, redirected: true };
        } else {
          result = { successful: false, errors: responseJson.messages, status: responseJson.status, failedRefresh: true };
        }
      }
    } catch (error) {
      result = { successful: false, errors: ["Error: " + error] };
    }

    debug("ensureValidAccessToken: Token refresh result: successful: " + result.successful + ", token: " + result.token);
    if (!result.successful) {
      dir(result);
    }

    return result;
  }
  if (DEBUGLEVEL.network) debug("< ensureValidAccessToken token is valid");
  return { successful: true, token: getLocalDataRemoteSession().token };
}

function handleErrorStatus(status, responseText) {
  if (DEBUGLEVEL.warnings) debug("handleErrorStatus response status: " + status + ", error: " + responseText);
  
  let messages_list = [];
  let message = "Error " + status;
  let detailCode = 0;

  try {
    let responseJson = JSON.parse(responseText);

    if (DEBUGLEVEL.warnings) debug("handleErrorStatus error response JSON: " + toJSONPretty(responseJson));

    if (responseJson) {
      if (responseJson.messages) {
        messages_list = responseJson.messages;
      } else if (responseJson.message) {
        message = responseJson.message;
      } else if (responseJson.error) {
        message = responseJson.error;
      } else {
        if (responseText) {
          message += " (" + responseText + ")";
        }
      }

      if ("detailCode" in responseJson) {
        detailCode = responseJson.detailCode;
      }
    } else {
      if (responseText) {
        message += " (" + responseText + ")";
      }
    }
  } catch (e) {
    if (responseText) {
      // This is usually HTML, so no reason
      // to add to the user message. It will
      // be logged above and sent in sendDebug,
      // unless it's short in which case it's
      // probably a simple error
      if (responseText.length < 150) {
        message += " (" + responseText + ")";
      } else {
        debug("Not bubbling up error because its length is " + responseText.length);
      }
      //message += " (" + responseText + ")";
    }
  }

  if (message && message.length > 200) {
    message = message.substring(0, 200) + " ... ";
  }

  var error = new Error(message);
  error.status = status;
  error.messages_list = messages_list;
  error.detailCode = detailCode;
  if (DEBUGLEVEL.warnings) debug("handleErrorStatus error: " + toJSONPretty(error));
  throw error;
}

function isStatusSuccess(status) {
  return status >= 200 && status < 300;
}

function addCommonQueryParameters(uri, skipVersion) {
  if (uri.indexOf("?v=") == -1 && uri.indexOf("&v=") == -1 && !skipVersion) {
    if (uri.indexOf('?') == -1) {
      uri += "?v=" + AppConstants.VERSION;
    } else {
      uri += "&v=" + AppConstants.VERSION;
    }
  }
  if (AppConstants.IS_DEVELOPMENT) {
    if (uri.indexOf('?') == -1) {
      uri += "?emulate_host=" + getLogicalHost(true);
    } else {
      uri += "&emulate_host=" + getLogicalHost(true);
    }
  }
  return uri;
}

async function fetchAuthorized(context, uri, method, postJson, formUrlEncoded, skipAudit, skipErrorStatusHandling, skipLogging) {
  return await fetchCustomized(context, uri, method, postJson, 1, formUrlEncoded, skipAudit, skipErrorStatusHandling, skipLogging);
}

async function fetchOptionallyAuthorized(context, uri, method, postJson, formUrlEncoded, skipAudit, skipErrorStatusHandling, skipLogging) {
  return await fetchCustomized(context, uri, method, postJson, 2, formUrlEncoded, skipAudit, skipErrorStatusHandling, skipLogging);
}

async function fetchUnauthorized(context, uri, method, postJson, formUrlEncoded, skipAudit, skipErrorStatusHandling, skipLogging) {
  return await fetchCustomized(context, uri, method, postJson, 0, formUrlEncoded, skipAudit, skipErrorStatusHandling, skipLogging);
}

async function fetchCustomized(context, uri, method, postJson, authorize, formUrlEncoded, skipAudit, skipErrorStatusHandling, skipLogging) {
  const urlMapping = urlMappings[uri];
  const actualRootUri = urlMapping && urlMapping.rootUri ? urlMapping.rootUri : (getActualHostLink(false, false) + '/');

  if (uri[0] == '/') {
    uri = uri.substring(1);
  }

  uri = addCommonQueryParameters(uri);

  if (!method) {
    method = "GET";
  }

  if (!skipLogging) {
    info("> fetchCustomized " + method + " " + actualRootUri + uri + " (" + context + ")");
  }

  if (postJson) {

    let debugJson = postJson;

    if ("password" in postJson) {
      debugJson = Object.assign({}, postJson);
      delete debugJson.password;
    }

    if ("newpassword" in postJson) {
      debugJson = Object.assign({}, postJson);
      delete debugJson.newpassword;
    }

    if (!skipAudit) {
      if (!skipLogging) {
        dir(debugJson);
      }
    }
  }

  let result = { successful: false };
  try {
    if (DEBUGLEVEL.network) debug("fetchCustomized authorize: " + authorize + ", formUrlEncoded: " + toJSONPretty(formUrlEncoded));

    let ready = true;
    let authorizationToken = null;

    if (authorize) {
      let performEnsureValidAccessToken = true;
      if (authorize == 2 && !hasAccessToken()) {
        performEnsureValidAccessToken = false;
      }

      if (performEnsureValidAccessToken) {
        if (DEBUGLEVEL.network) debug("fetchCustomized calling ensureValidAccessToken");

        try {
          result = await ensureValidAccessToken();
          if (result.token) {
            authorizationToken = result.token;
          } else {
            if (authorize == 1) {
              dir(result);
              throw new Error("Access token could not be refreshed. Please clear your cache, reload and log in again.");
            }
          }
        } catch (e) {
          if (authorize == 1) {
            dir(result);
            throw e;
          }
        }

        if (DEBUGLEVEL.network) debug("fetchCustomized ensureValidAccessToken returned " + ready);
      }
    }
    
    if (ready) {
      let headers = {
        "Accept": "application/json",
        "Content-Type": "application/json",
        //"Host": actualHost,
      };

      if (authorizationToken) {
        headers["Authorization"] = "Bearer " + authorizationToken;
      }

      const savedToken = authorizationToken;

      let fetchParams = {
        method: method,
        headers: headers,
      };

      if (formUrlEncoded) {
        headers["Content-Type"] = "application/x-www-form-urlencoded";
        fetchParams.body = formUrlEncoded.toString();
      } else if (postJson) {
        fetchParams.body = toJSONPretty(postJson);
      }

      // Timeout in RN v0.60.4; wait until it's available in Expo
      // https://github.com/facebook/react-native/commit/5e36b0c6eb2494cefd11907673aa018831526750
      //const controller = new AbortController();
      //const signal = controller.signal;
      //signal: signal,
      //const timeoutId = setTimeout(() => controller.abort(), 30000);
      //clearTimeout(timeoutId);

      try {

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MILLISECONDS_MAIN /* ms */);
        fetchParams.signal = controller.signal;

        const response = await fetch(
          actualRootUri + uri,
          fetchParams,
        );
  
        clearTimeout(timeoutId);

        const status = response.status;
  
        if (!skipLogging) {
          info("< fetchCustomized response: " + status + " (" + context + ")");
        }
  
        const responseText = await response.text();
  
        if (isStatusSuccess(status)) {

          const responseBody = JSON.parse(responseText);

          if (DEBUGLEVEL.network || DEBUGLEVEL.dev) {
            if (!skipLogging) {
              debug("fetchCustomized response success:");
              dir(responseBody);
            }
          }

          result = {
            status: status,
            successful: true,
            body: responseBody,
            token: savedToken,
          };
        } else {
          if (skipErrorStatusHandling) {
            result = {
              status: status,
              successful: false,
              responseText: responseText,
            };
          } else {
            handleErrorStatus(status, responseText);
          }
        }
      } catch (error) {
        if (DEBUGLEVEL.warnings) debug("< fetchCustomized error: url: " + uri + ", error: " + getErrors(error, true));

        result = {
          status: 500,
          successful: false,
          status: error.status,
          detailCode: "detailCode" in error ? error.detailCode : 0,
          errors: error.messages_list && error.messages_list.length > 0 ? error.messages_list : [ error.message ],
        };

        var finalError = new Error(result.errors.join(", "));
        finalError.status = result.status;
        finalError.detailCode = result.detailCode;
        finalError.errors = result.errors;
        finalError.underlyingException = error;
        throw finalError;
      }
    } else {
      if (DEBUGLEVEL.network) debug("fetchCustomized not ready");

      var finalError = new Error(result.errors.join(", "));
      finalError.status = result.status;
      finalError.detailCode = result.detailCode;
      finalError.errors = result.errors;
      throw finalError;
    }
  } catch (e) {
    if (e.errors) {
      throw e;
    } else {
      var finalError = new Error(e.toString());
      finalError.status = result.status;
      finalError.detailCode = result.detailCode;
      finalError.errors = result.errors;
      finalError.underlyingException = e;
      throw finalError;
    }
  }

  if (DEBUGLEVEL.network) {
    debug("< fetchCustomized result:");
    dir(result);
  }

  return result;
}

function getHeight() {
  return Dimensions.get("window").height;
}

function getWidth() {
  return Dimensions.get("window").width;
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function getAccumulatedDebug() {
  return accumulatedDebug;
}

function getUUID() {
  return uuidv4();
}

function createRequestUrl(url) {
  if (url.includes("?")) {
    url += "&emulate_host=rabbl.com";
  } else {
    url += "?emulate_host=rabbl.com";
  }
  return url;
}

// [min, max]
function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

function getHeaderRight(navigation) {
  return null;
}

function debugObject(obj, name) {
  try {
    info("debugObject stringifying " + name);
    for (var prop in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, prop)) {
        try {
          JSON.stringify(obj[prop]);
          info("  debugObject " + prop + ": " + toJSONPretty(obj[prop]));
        } catch (e) {
          info("  debugObject could not stringify " + prop);
        }
      }
    }
  } catch (e) {
    handleException(e, "debugObject");
  }
}

function escapeHTML(unsafe) {
  if (isNullOrUndefined(unsafe)) {
    return "";
  } else {
    return unsafe.replace(
      /[\u0000-\u002F\u003A-\u0040\u005B-\u0060\u007B-\u00FF]/g,
      c => '&#' + ('000' + c.charCodeAt(0)).slice(-4) + ';'
    );
  }
}

// compare
function areObjectsEqual(x, y) {
  for (var prop in x) {
    if (x[prop] !== y[prop]) {
      return false;
    }
  }
  for (var prop in y) {
    if (x[prop] !== y[prop]) {
      return false;
    }
  }
  return true;
}

// utmCampaign: The individual campaign name, slogan, promo code, etc. for a product.
// utmMedium (optional): The advertising or marketing medium, for example:
//   referral, affiliate, cpc, banner, email, social, newsletter, blog_comments, cpm, display, organic, ad (https://cxl.com/wp-content/uploads/2020/02/Screen-Shot-2020-02-28-at-12.06.14-PM.png)
//   Default: referral: "Used to identify traffic from websites not known to be a search engine or social media platform."
function getLinkWithUTM(link, utmCampaign, utmMedium) {
  if (link.indexOf("utm_source") == -1) {
    if (link.indexOf("?") == -1) {
      link += "?";
    } else {
      link += "&";
    }
    if (!utmMedium) {
      utmMedium = AppConstants.UTM_MEDIUMS.REFERRAL;
    }
    if (!utmCampaign) {
      utmCampaign = AppConstants.UTM_CAMPAIGNS.GENERAL;
    }
    link += "utm_source=" + AppConstants.UTM_SOURCES.FINDHUMANE;
    link += "&utm_medium=" + utmMedium;
    link += "&utm_campaign=" + utmCampaign;
  }
  return link;
}

function openLink(link, utmCampaign, utmMedium) {
  link = getLinkWithUTM(link, utmCampaign, utmMedium);
  info("openLink: " + link);
  if (isWeb()) {
    window.open(link, '_blank');
  } else {
    Linking.openURL(link);
  }
}

function createBackendLink(
           children,
           link,
           utmCampaign,
           utmMedium,
           skipUTM,
           linkStyle,
           key
         ) {
  if (isWeb()) {
    if (link.startsWith("http") && !skipUTM) {
      link = getLinkWithUTM(link, utmCampaign, utmMedium);
    }
    const linkProps = {};
    if (linkStyle) {
      linkProps.style = linkStyle;
    }
    return (
      <a
        key={key}
        href={link}
        target="_blank"
        {...linkProps}
      >{children}</a>
    );
  } else {
    const linkProps = {};
    if (isString(children)) {
      return (
        <Text
          key={key}
          style={AppConstants.STYLES.link}
          onPress={() => openLink(link, utmCampaign, utmMedium)}
          {...linkProps}
        >{children}</Text>
      );
    } else {
      return (
        <TouchableOpacity
          key={key}
          onPress={() => openLink(link, utmCampaign, utmMedium)}
          {...linkProps}
        >{children}</TouchableOpacity>
      );
    }
  }
}

async function recordAppPageView(urlFragment) {

  // No-op for calls from the web as those page views
  // are transparently tracked
  if (isWeb()) {
    return;
  }

  info("recordAppPageView started for " + urlFragment);

  try {
    const userAgent = await getUserAgent();

    // Response from Plausible:
    // "our API docs are slightly wrong in that they are written
    // with server-side and proxy use-cases in mind. We do infer the IP
    // if it is not explicitly set"
    // 
    //let ipAddress = await getPublicIPAddress();
    //if (!ipAddress) {
    //  ipAddress = await getIPAddress();
    //}

    //info("recordAppPageView IP address: " + ipAddress + ", user agent: " + userAgent);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000 /* ms */);
    const url = "app://localhost/" + urlFragment;
    let referrer = null;
    if (isIOS()) {
      referrer = "https://www.apple.com/ios/";
    } else {
      referrer = "https://www.android.com/";
    }

    // https://plausible.io/docs/events-api
    const response = await fetch(
      "https://plausible.io/api/event",
      {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "User-Agent": userAgent,

          // See comments above
          //"X-Forwarded-For": ipAddress,
        },
        body: JSON.stringify({
          name: "pageview",
          domain: AppConstants.HOST,
          url: url,
          referrer: referrer,
          props: {
            isAppPageView: true,
            isAndroid: isAndroid(),
            isIOS: isIOS(),
          }
        }),
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (response.status == 200 || response.status == 202) {
      info("recordAppPageView completed to " + url);
    } else {
      let plausibleError = "Plausible returned " + response.status + " for " + urlFragment + "\n\nResponse headers:";
      for (let header of response.headers.entries()) {
        plausibleError += "\n" + header[0]+ ': '+ header[1];
      }
      plausibleError += "\n\nResponse text:\n\n";
      plausibleError += await response.text();
      reportIssue(plausibleError);
    }

  } catch (e) {
    info("recordAppPageView error to " + urlFragment + ": " + getErrors(e));
  }
}

async function getUserAgent() {
  if (isAndroid() || isIOS()) {
    return await DeviceInfo.getUserAgent();
  } else if (isWeb()) {
    return window.navigator.userAgent;
  } else {
    throw new Error("Unknown platform");
  }
}

const charToFullWidth = char => {
	const c = char.charCodeAt( 0 )
	return c >= 33 && c <= 126
		? String.fromCharCode( ( c - 33 ) + 65281 )
		: char
};

const stringToFullWidth = string => string.split( '' ).map( charToFullWidth ).join( '' );

export {
  DEBUGLEVEL,
  info,
  debug,
  dir,
  logException,
  isAndroid,
  isIOS,
  isWeb,
  isWebAndroid,
  isWebiOS,
  shouldAutoFocus,
  handleException,
  fetchAuthorized,
  fetchOptionallyAuthorized,
  fetchUnauthorized,
  handleErrorStatus,
  isStatusSuccess,
  toJSONPretty,
  getHeight,
  getWidth,
  sleep,
  login,
  logout,
  getErrors,
  getAccumulatedDebug,
  loadLocalData,
  updateLocalData,
  getLocalData,
  getAllLocalData,
  getUUID,
  getLocalDataRemoteSession,
  createRequestUrl,
  randomInt,
  getHeaderRight,
  sendDebugDirect,
  openLink,
  createBackendLink,
  setStoredValue,
  removeStoredValue,
  removeLocalData,
  reportIssue,
  debugObject,
  isUndefined,
  isDefined,
  isNull,
  isBlank,
  isString,
  isNullOrUndefined,
  escapeHTML,
  getLinkWithUTM,
  setExtraDebug,
  updateSavedAppState,
  getSavedAppState,
  recordAppPageView,
  isAccessTokenExpired,
  hasAccessToken,
  ensureValidAccessToken,
  handleError,
  getActualHost,
  getActualHostLink,
  getLogicalHost,
  addCommonQueryParameters,
  stringToFullWidth,
  getLocalDataDomain,
  setLocalDataDomain,
  getStoredValue,
  getNativeCurrentDomain,
}
